import { renderWeekSheet, sheetFileName, type SheetInput } from '@aurora/core'
import { auroraStore } from '../store/auroraStore'
import { activeChild, mondayOf, planWeeks, rubricDomains, weekBlocks } from '../store/selectors'

/**
 * Print this week as the paper sheets the family started with.
 *
 * Built from the same rows the Today view shows, rendered by the shared
 * sheet module, and handed to the browser's print dialog in its own window
 * so the app's styles never leak onto the page.
 */
export function sheetInputFor(date: string): SheetInput | null {
  const s = auroraStore.getState()
  const child = activeChild(s)
  if (!child) return null
  const monday = mondayOf(date)
  const week = planWeeks(s).find((w) => w.days.some((d) => d.date === date))
  const dayRows = weekBlocks(s, monday)
  return {
    childName: child.name,
    monday,
    week: week
      ? {
          number: week.number,
          theme: week.theme,
          focus: week.focus.map((f) => f.statement).join(' '),
          startDate: week.startDate,
          materialsList: week.materials,
          checkinQuestions: week.checkinQuestions,
        }
      : undefined,
    days: dayRows.map((d) => ({
      date: d.date,
      caregiving: week?.days.find((x) => x.date === d.date)?.caregiving,
      blocks: d.blocks.map((b, i) => ({
        slot: i,
        title: b.title,
        instructions: b.instructions,
        minutes: b.minutes,
        domainName: b.domainName ?? b.domain,
        status: b.status,
        note: b.note,
        materials: b.materials,
      })),
    })),
    domains: rubricDomains(s).map((d) => ({ key: d.key, name: d.name })),
    generatedAt: Date.now(),
  }
}

export function printWeekSheet(date: string): boolean {
  const input = sheetInputFor(date)
  if (!input) return false
  const html = renderWeekSheet(input)
  const win = window.open('', '_blank')
  if (!win) return false
  win.document.open()
  win.document.write(html)
  win.document.close()
  win.document.title = sheetFileName(input)
  // Fonts and layout settle before the dialog opens; without the wait some
  // browsers print a blank first page.
  win.addEventListener('load', () => setTimeout(() => win.print(), 150))
  setTimeout(() => { try { win.print() } catch { /* already printing */ } }, 600)
  return true
}
